/**
 * The Payer Mappings board's pure helpers: which of the four groups a rule
 * belongs to, the order the board lists them in, and the facts each row
 * states — who wrote the rule, what the check behind it answered, who
 * decided on it. Pure so the board's rendering rules are testable without
 * the component, and so the board file exports only the component.
 */
import type { PayerAliasBoardRow } from '../services/payerAliases'

/** The four groups, in the order the board lists them. */
export type RuleGroup = 'unreviewed' | 'rejected' | 'approved' | 'staff'
export const GROUP_ORDER: RuleGroup[] = ['unreviewed', 'rejected', 'approved', 'staff']
export const GROUP_LABEL: Record<RuleGroup, string> = {
  unreviewed: 'Unreviewed',
  rejected: 'Rejected',
  approved: 'Approved',
  staff: 'Set by hand',
}

export function groupOf(row: Pick<PayerAliasBoardRow, 'source' | 'reviewState'>): RuleGroup {
  if (row.source === 'staff' || row.reviewState === null) return 'staff'
  return row.reviewState
}

/** Unreviewed first, then rejected, approved, set by hand; newest first
 *  within each group, so a rule learned since the operator last looked tops
 *  its group. */
export function orderRows(rows: PayerAliasBoardRow[]): PayerAliasBoardRow[] {
  return [...rows].sort((a, b) =>
    GROUP_ORDER.indexOf(groupOf(a)) - GROUP_ORDER.indexOf(groupOf(b)) ||
    (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
}

/** Who wrote the rule: the two parts of the system, a person, or not known. */
export function writerLabel(row: Pick<PayerAliasBoardRow, 'source' | 'learned'>): string {
  if (row.source === 'staff') return 'A person'
  if (row.learned?.by === 'success_learning') return 'Success learning'
  if (row.learned?.by === 'ai_suggestion') return 'AI suggestion'
  return 'Not known'
}

/** What the check behind the rule answered — stated, never judged. A rule
 *  set by hand has no check behind it. */
export function answerLabel(row: Pick<PayerAliasBoardRow, 'source' | 'learned'>): string {
  if (row.source === 'staff') return '—'
  switch (row.learned?.answer) {
    case 'eligible': return 'Eligible'
    case 'not_eligible': return 'Not eligible'
    case 'eligible_no_benefits': return 'Eligible, no benefits returned'
    default: return 'Not known'
  }
}

export function fmtDecidedDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Who made the last decision on the rule, and when — the operator who
 *  approved or rejected it, or the person, at the practice or at VitalRev,
 *  who set it by hand. "Not known" for a rule from before this was recorded;
 *  "—" for a self-written rule nobody has decided on yet. */
export function decidedLabel(row: Pick<PayerAliasBoardRow, 'source' | 'reviewState' | 'decidedBy'>): string {
  const d = row.decidedBy
  if (!d) {
    return row.source === 'staff' || (row.reviewState !== null && row.reviewState !== 'unreviewed')
      ? 'Not known'
      : '—'
  }
  return `${d.email || d.uid} · ${d.atVitalRev ? 'at VitalRev' : 'at the practice'} · ${fmtDecidedDate(d.at)}`
}
